/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import {
    Component,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import {
    IReplaceStringVar,
    ISearchReplacePair,
    ISearchStringVar,
} from 'generated-types';

import { SearchReplacePair } from 'object-types';

import {
    MessageItem,
    TMessageItem,
} from 'ajs/modules/data-model/factories/message-item.factory';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import * as l10n from './search-replace-pair.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Partial type of Search Replace Pair.
 */
type TPartialSearchReplacePair = Omit<ISearchReplacePair, 'replacement_string' | 'search_string'>;

/**
 * Search Replace Pair Config type.
 */
interface ISearchReplacePairConfig extends TPartialSearchReplacePair {
    replacement_string: MessageItem<IReplaceStringVar>;
    search_string: MessageItem<ISearchStringVar>;
}

/**
 * @description
 *
 *   Search Replace Pair Message Item.
 *
 * @author Sarthak kapoor
 */
export class SearchReplacePairConfigItem extends
    withEditChildMessageItemMixin<ISearchReplacePairConfig, TMessageItem>(
        withFullModalMixin(MessageItem),
    )<ISearchReplacePairConfig> {
    public static ngDependencies = [
        'defaultValues',
        L10nService,
    ];

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: SearchReplacePair,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getNgDependency(L10nService);
        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<ISearchReplacePairConfig> {
        const defaultValues = this.getNgDependency('defaultValues');
        const defaultSearchReplacePairConfig = defaultValues.getDefaultItemConfigByType(
            this.objectType,
        ) || {};
        const { replacement_string: replacementString } = defaultSearchReplacePairConfig;

        replacementString.type = undefined;

        return {
            ...defaultSearchReplacePairConfig,
            replacement_string: replacementString,
        };
    }

    /**
     * Import lazy loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            VsSearchReplaceStringModalComponent,
        } = await import(
            /* webpackChunkName: "virtual-service-modal" */
            /* eslint-disable-next-line max-len */
            'ng/lazy-loaded-components/modals/virtual-service-modal/vs-content-rewrite-rules/vs-search-replace-string-modal/vs-search-replace-string-modal.component'
        );

        return VsSearchReplaceStringModalComponent as Type<Component>;
    }

    /** @override */
    protected modifyConfigDataBeforeSave(): void {
        super.modifyConfigDataBeforeSave();

        if (this.config.replacement_string && !this.config.replacement_string.config.val) {
            delete this.config.replacement_string;
        }
    }

    /** @override */
    protected requiredFields(): string[] {
        return [
            'replacement_string',
            'search_string',
        ];
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.searchReplaceStringBreadCrumbTitle);
    }
}
